import React, { createContext, ReactNode, useContext } from 'react';

/**
 * Permissions the module needs, decoupled from how the host app derives
 * them. The host mounts `<PermissionsProvider>` at the point where it
 * renders `<PartnerDataGatewayModule />` and passes in whatever it computed
 * from Okta roles / `useROIRoleBasedRoutes`. Nothing inside this module may
 * import `useROIRoleBasedRoutes` or read Okta claims directly — see the
 * module README for the full portability rules.
 */
export interface PartnerDataGatewayPermissions {
  canViewIngestions: boolean;
  canControlIngestions: boolean;
  canViewConfigurations: boolean;
  canManageConfigurations: boolean;
  /**
   * Whether the user may see Protected Health Information — today the raw
   * parsed rows behind the ingestion detail's Records tab. Mirrors the
   * `GqlPhiGuard` the BFF puts on `pdgIngestionRecords`: without it that
   * query errors, so the UI hides the tab instead of offering a dead end.
   */
  canViewPhi: boolean;
}

export const DEFAULT_PARTNER_DATA_GATEWAY_PERMISSIONS: PartnerDataGatewayPermissions =
  {
    canViewIngestions: false,
    canControlIngestions: false,
    canViewConfigurations: false,
    canManageConfigurations: false,
    canViewPhi: false,
  };

const PermissionsContext = createContext<PartnerDataGatewayPermissions>(
  DEFAULT_PARTNER_DATA_GATEWAY_PERMISSIONS,
);

export interface PermissionsProviderProps {
  permissions: PartnerDataGatewayPermissions;
  children: ReactNode;
}

export const PermissionsProvider = ({
  permissions,
  children,
}: PermissionsProviderProps): JSX.Element => (
  <PermissionsContext.Provider value={permissions}>
    {children}
  </PermissionsContext.Provider>
);

export const usePermissions = (): PartnerDataGatewayPermissions =>
  useContext(PermissionsContext);
